import { normalizeUnit } from './units';

/** Tipos de insumo del catálogo (RES-22). Mismo catálogo cerrado que el backend. */
export const KINDS = ['INGREDIENT', 'RESALE', 'PACKAGING'];

/** Estado vacío del formulario de insumo. */
export const EMPTY_INVENTORY_FORM = {
  name: '',
  kind: 'INGREDIENT',
  unit: 'un',
  category: '',
  supplierId: '',
  sku: '',
  refCost: '',
  defaultMinStock: '',
  minStockOverride: '',
  trackStock: true,
  isActive: true,
  stock: '',
  expirationDate: '',
};

/**
 * Normaliza un insumo del API para el formulario. Los datos viejos pueden traer `gr`/`lt`,
 * `cost`/`minStock` en vez de `refCost`/`defaultMinStock`, o no tener `kind` ni flags.
 * Con stock por local (RES-23) `minStockOverride` es el mínimo propio del restaurante (o vacío
 * si hereda) y `defaultMinStock` el de la organización.
 */
export function prepareInventoryItemForEdit(item) {
  return {
    name: item.name || '',
    kind: KINDS.includes(item.kind) ? item.kind : 'INGREDIENT',
    unit: normalizeUnit(item.unit) || 'un',
    category: item.category || '',
    supplierId: item.supplierId || '',
    sku: item.sku || '',
    refCost: item.refCost ?? item.cost ?? '',
    defaultMinStock:
      item.defaultMinStock ?? (item.minStockInherited === false ? '' : (item.minStock ?? '')),
    minStockOverride:
      item.minStockOverride !== undefined && item.minStockOverride !== null
        ? item.minStockOverride
        : '',
    trackStock: item.trackStock !== false,
    isActive: item.isActive !== false,
    stock: item.stock ?? '',
    expirationDate: item.expirationDate ? String(item.expirationDate).split('T')[0] : '',
  };
}

/**
 * Arma el payload para `POST/PUT /inventory`: números parseados, textos recortados, vacíos → null.
 * `stock` y `expirationDate` solo viajan si el insumo controla stock; el mínimo del local se
 * guarda aparte (`PUT /inventory/stock/{itemId}`, ver `buildLocalMinStockChange`).
 */
export function buildInventoryPayload(form, { isEdit = false } = {}) {
  const payload = {
    name: String(form.name || '').trim(),
    kind: KINDS.includes(form.kind) ? form.kind : 'INGREDIENT',
    unit: form.unit,
    category: String(form.category || '').trim() || null,
    supplierId: form.supplierId || null,
    sku: String(form.sku || '').trim() || null,
    refCost: form.refCost === '' || form.refCost === null ? 0 : Number(form.refCost),
    defaultMinStock:
      form.defaultMinStock === '' || form.defaultMinStock === null
        ? 0
        : Number(form.defaultMinStock),
    trackStock: form.trackStock !== false,
  };
  if (isEdit) payload.isActive = form.isActive !== false;
  if (payload.trackStock) {
    if (form.stock !== '' && form.stock !== null && form.stock !== undefined) {
      payload.stock = Number(form.stock);
    }
    if (form.expirationDate) payload.expirationDate = form.expirationDate;
    else if (isEdit) payload.expirationDate = null;
  }
  return payload;
}

/**
 * Qué mandar a `PUT /inventory/stock/{itemId}` según el mínimo del local del formulario:
 * `undefined` si no cambió, `{ minStock: null }` para volver a heredar, `{ minStock: n }` si se fijó.
 */
export function buildLocalMinStockChange(form, item) {
  const before =
    item && item.minStockOverride !== undefined && item.minStockOverride !== null
      ? Number(item.minStockOverride)
      : null;
  const raw = form.minStockOverride;
  const after = raw === '' || raw === null || raw === undefined ? null : Number(raw);
  if (after !== null && !Number.isFinite(after)) return undefined;
  if (before === after) return undefined;
  return { minStock: after };
}

/** Mínimo efectivo: el que devuelve el API por local (`minStock`) o el del catálogo. */
export function effectiveMinStock(item) {
  if (item.minStockInherited === false && typeof item.minStock === 'number') return item.minStock;
  return Number(item.minStock ?? item.defaultMinStock ?? 0) || 0;
}

/**
 * Estado que muestra la tabla: desactivado y "sin control de stock" antes que los estados de stock.
 * @param {Object} item - Insumo del API
 * @param {'expired'|'critical'|'warning'|'ok'|null} expirationStatus - Estado del vencimiento
 * @returns {'inactive'|'notTracked'|'outOfStock'|'lowStock'|'expiringSoon'|'expired'|'normalStock'}
 */
export function getStockStatus(item, expirationStatus) {
  if (item.isActive === false) return 'inactive';
  if (item.trackStock === false) return 'notTracked';
  const stock = Number(item.stock) || 0;
  if (stock === 0) return 'outOfStock';
  if (stock <= effectiveMinStock(item)) return 'lowStock';
  if (expirationStatus === 'critical' || expirationStatus === 'warning') return 'expiringSoon';
  if (expirationStatus === 'expired') return 'expired';
  return 'normalStock';
}
